/**
 * CalendarMonthGrid — the Practice calendar's Month view (PROD-3307 C6), a PA-local port of PM
 * `src/components/blocks/month-calendar.tsx` (origin/main 5a49b124). The designer's ruling (21 Oct): the calendar's
 * reference for look and behaviour is PM React.
 *
 * LOCAL STAND-IN FOR DS: the Signal DS `PACalendarMonth` (2.29.1) is the earlier graduation of this block and has drifted
 * from PM since (cell height, weekend / past greys, the chip's profile bar and tooltip, the loading / error line). Same
 * props and data shapes as `PACalendarMonth` (`PACalendarMonthDay` / `PACalendarMonthEvent`, built with the DS
 * `buildMonthGrid`), so it can be swapped back once the DS block catches up.
 *
 * 6 × 7 grid, Monday first. Each cell: the day number (today on a filled brand disc), the events the server returned
 * (it caps appointments at 3 a day; blocks are all shown) as chips — the profile-colour bar, the patient name, then the
 * time range, the time as the chip's tooltip — and "+N more" from the server's `group.count`. Outside the month and
 * weekends draw on the grey panel; a past day's chips lose their colour. A chip opens the appointment; the date, "+N
 * more" or empty space drops to Day on that date.
 *
 * PM paints the chip bar with an inline `backgroundColor`; this repo allows no inline style in a new file, so the bar
 * carries its hue (`data-hue`, the same `categoryHue` PM's `categoryColor(…, 500, cat-blue-500)` resolves) and
 * `showcase-calendar.css` paints it with the same `--cat-<hue>-500` token.
 */
import type { MouseEvent, ReactNode } from 'react';
import { Ban, Repeat } from 'lucide-react';
import {
  BLOCK_STRIPES,
  toLocalISO,
  type PACalendarMonthDay,
  type PACalendarMonthEvent,
} from '@scripsteam/scrips-signal-ds';
import { categoryHue } from '@/shared/lib/category-color';
import { cn } from '@/lib/utils';

export interface CalendarMonthGridProps {
  /** 42 entries (6×7), from the DS `buildMonthGrid`. */
  days: PACalendarMonthDay[];
  /** The month's name, the grid's accessible label ("October 2026"). */
  monthLabel: string;
  isLoading?: boolean;
  isError?: boolean;
  /** Drop to Day view on a date (date / "+N more" / empty space). */
  onDayClick?: (iso: string) => void;
  /** Open an appointment (or a block) from its chip. */
  onEventClick?: (event: PACalendarMonthEvent) => void;
  /** Kept for the `PACalendarMonth` prop shape; PM's month draws its own quiet "Loading…" line. */
  loadingIndicator?: ReactNode;
}

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function CalendarMonthGrid({
  days,
  monthLabel,
  isLoading = false,
  isError = false,
  onDayClick,
  onEventClick,
}: CalendarMonthGridProps) {
  // Read at render, not at module load: a tab left open past midnight still greys the right days.
  const today = toLocalISO(new Date());
  return (
    <div data-slot="pa-calendar-month" aria-label={monthLabel} className="flex flex-col flex-1 min-h-0 bg-surface-card">
      {isLoading || isError ? (
        <div className="px-4 py-2 border-b border-border-default">
          {isLoading ? (
            <span className="cal-month-meta text-text-muted">Loading…</span>
          ) : null}
          {isError ? (
            <span className="cal-month-meta text-status-error" role="alert">
              Couldn't load — try toggling the date.
            </span>
          ) : null}
        </div>
      ) : null}

      <div className="sticky top-0 z-10 grid grid-cols-7 border-b border-border-default bg-surface-card">
        {WEEKDAY_LABELS.map((label) => (
          <div
            key={label}
            className={cn(
              'flex h-10 items-center px-3 cal-month-label font-medium text-text-secondary',
              label !== 'Mon' && 'border-l border-border-subtle',
            )}
          >
            {label}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 grid-rows-6 flex-1 min-h-0 overflow-y-auto scrollbar-hide">
        {days.map((day, i) => (
          <MonthCell
            key={day.iso}
            day={day}
            weekend={i % 7 > 4}
            past={day.iso < today}
            isToday={day.iso === today}
            onDayClick={onDayClick}
            onEventClick={onEventClick}
            isLoading={isLoading}
          />
        ))}
      </div>
    </div>
  );
}

interface MonthCellProps {
  day: PACalendarMonthDay;
  weekend?: boolean;
  past?: boolean;
  isToday?: boolean;
  onDayClick?: (iso: string) => void;
  onEventClick?: (event: PACalendarMonthEvent) => void;
  isLoading?: boolean;
}

function MonthCell({ day, weekend, past, isToday, onDayClick, onEventClick, isLoading }: MonthCellProps) {
  const goToDay = () => {
    if (!isLoading) onDayClick?.(day.iso);
  };
  return (
    // A div (not a button) so the chips can be real buttons; empty space drops to Day view.
    <div
      data-slot="pa-calendar-month-cell"
      data-iso={day.iso}
      onClick={goToDay}
      className={cn(
        // PM's 112 px minimum cell height = 28 steps of the DS `--spacing` (0.25rem) = 7rem.
        'flex flex-col items-stretch text-left p-2 gap-1 border-r border-b border-border-subtle last:border-r-0 min-h-28',
        day.inMonth && !weekend ? 'bg-surface-card' : 'bg-grey-100',
        !isLoading && 'cursor-pointer hover:bg-blue-100/30',
        isLoading && 'animate-pulse',
      )}
    >
      <div className="flex items-center justify-between gap-1">
        <span
          data-today={isToday ? '' : undefined}
          className={cn(
            'inline-flex h-6 min-w-6 items-center justify-center rounded-full px-1 cal-month-label font-medium tabular-nums',
            isToday ? 'bg-brand-primary text-text-inverse' : day.inMonth ? 'text-text-primary' : 'text-text-hint',
          )}
        >
          {day.date}
        </span>
      </div>
      <div className="flex flex-col gap-1 min-w-0">
        {day.events.map((event) => (
          <MonthEventChip key={event.id} event={event} past={past} onClick={onEventClick} />
        ))}
        {day.moreCount > 0 ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goToDay();
            }}
            className="rounded-sm px-1 py-0.5 text-left text-xs font-semibold text-brand-primary hover:bg-blue-100/50"
          >
            +{day.moreCount} more
          </button>
        ) : null}
      </div>
    </div>
  );
}

function MonthEventChip({
  event,
  past,
  onClick,
}: {
  event: PACalendarMonthEvent;
  past?: boolean;
  onClick?: (event: PACalendarMonthEvent) => void;
}) {
  const handle = (e: MouseEvent) => {
    e.stopPropagation();
    onClick?.(event);
  };
  if (event.isBlock) {
    const label = event.label?.trim() || 'Blocked';
    return (
      <button
        type="button"
        data-slot="pa-calendar-month-chip"
        data-block=""
        onClick={handle}
        // A block's reason is free text that can carry PHI: kept out of the tooltip (PM, PROD-794).
        title={`Blocked · ${event.timeRange}`}
        className={cn('flex w-full items-center gap-1 overflow-hidden rounded-sm px-1 py-0.5 text-left hover:shadow-sm', BLOCK_STRIPES)}
      >
        {event.isRecurring ? (
          <Repeat className="h-2 w-2 shrink-0 text-text-muted" aria-label="Recurring block" />
        ) : (
          <Ban className="h-2 w-2 shrink-0 text-text-muted" />
        )}
        <span className="flex-1 truncate text-xs font-medium text-text-secondary">{label}</span>
      </button>
    );
  }
  return (
    <button
      type="button"
      data-slot="pa-calendar-month-chip"
      onClick={handle}
      // The patient name stays in the visible chip only; the tooltip is the time (PM, PROD-794).
      title={event.timeRange}
      className="flex w-full items-center gap-1 overflow-hidden rounded-sm border border-border-subtle bg-surface-card px-1 py-0.5 text-left hover:bg-grey-100"
    >
      <span
        data-slot="pa-calendar-month-chip-bar"
        data-hue={categoryHue(event.profileColor) ?? 'blue'}
        data-past={past ? '' : undefined}
        className="h-3 w-0.5 shrink-0 rounded-full"
        aria-hidden
      />
      <span className={cn('flex-1 truncate text-xs font-medium', past ? 'text-text-secondary' : 'text-text-primary')}>
        {event.label || 'Unknown'}
      </span>
      <span className="shrink-0 text-xs text-text-muted">{event.timeRange}</span>
    </button>
  );
}
